import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import AppShell from '../components/layout/AppShell';
import ProtectedRoute from './ProtectedRoute';
import LoginScreen from '../screens/LoginScreen';
import { SuspenseWrap } from './SuspenseWrap';

const DashboardScreen = lazy(() => import('../screens/DashboardScreen'));
const UserManagementScreen = lazy(() => import('../screens/UserManagementScreen'));
const ApprovalsScreen = lazy(() => import('../screens/ApprovalsScreen'));
const CompanyManagementScreen = lazy(() => import('../screens/CompanyManagementScreen'));
const CompanyProjectsScreen = lazy(() => import('../screens/CompanyProjectsScreen'));
const AdminProjectDetailsScreen = lazy(() => import('../screens/AdminProjectDetailsScreen'));
const ExportScreen = lazy(() => import('../screens/ExportScreen'));
const AuditLogsScreen = lazy(() => import('../screens/AuditLogsScreen'));
const ConfigScreen = lazy(() => import('../screens/ConfigScreen'));
const ScrapersScreen = lazy(() => import('../screens/ScrapersScreen'));
const FxManagementScreen = lazy(() => import('../screens/FxManagementScreen'));
const ReMatrixScreen = lazy(() => import('../screens/ReMatrixScreen'));
const SystemMessagesScreen = lazy(() => import('../screens/SystemMessagesScreen'));
const ExperimentalFeaturesScreen = lazy(() => import('../screens/ExperimentalFeaturesScreen'));
const ExperimentsScreen = lazy(() => import('../screens/ExperimentsScreen'));
const TranslationsScreen = lazy(() => import('../screens/TranslationsScreen'));
const PremiumScreen = lazy(() => import('../screens/settings/PremiumScreen'));
const ShieldDashboardScreen = lazy(() => import('../screens/ShieldDashboardScreen'));
const ShieldAdminReviewsScreen = lazy(() => import('../screens/ShieldAdminReviewsScreen'));
const ShieldReportRunnerScreen = lazy(() => import('../screens/ShieldReportRunnerScreen'));
const ShieldPlaybookRunnerScreen = lazy(() => import('../screens/ShieldPlaybookRunnerScreen'));
const EditRequestsScreen = lazy(() => import('../screens/EditRequestsScreen'));

export const router = createBrowserRouter([
  { path: '/login', element: <LoginScreen /> },
  {
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '/',
        element: (
          <SuspenseWrap>
            <DashboardScreen />
          </SuspenseWrap>
        ),
      },
      {
        path: '/users',
        element: (
          <SuspenseWrap>
            <UserManagementScreen />
          </SuspenseWrap>
        ),
      },
      {
        path: '/approvals',
        element: (
          <SuspenseWrap>
            <ApprovalsScreen />
          </SuspenseWrap>
        ),
      },
      {
        path: '/edit-requests',
        element: (
          <SuspenseWrap>
            <EditRequestsScreen />
          </SuspenseWrap>
        ),
      },
      {
        path: '/companies',
        element: (
          <SuspenseWrap>
            <CompanyManagementScreen />
          </SuspenseWrap>
        ),
      },
      {
        path: '/companies/:companyId/projects',
        element: (
          <SuspenseWrap>
            <CompanyProjectsScreen />
          </SuspenseWrap>
        ),
      },
      {
        path: '/companies/audit',
        element: (
          <SuspenseWrap>
            <AuditLogsScreen />
          </SuspenseWrap>
        ),
      },
      {
        path: '/projects/:projectId',
        element: (
          <SuspenseWrap>
            <AdminProjectDetailsScreen />
          </SuspenseWrap>
        ),
      },
      {
        path: '/scrapers',
        element: (
          <SuspenseWrap>
            <ScrapersScreen />
          </SuspenseWrap>
        ),
      },
      {
        path: '/shield',
        element: (
          <SuspenseWrap>
            <ShieldDashboardScreen />
          </SuspenseWrap>
        ),
      },
      {
        path: '/admin/shield-admin-reviews',
        element: (
          <SuspenseWrap>
            <ShieldAdminReviewsScreen />
          </SuspenseWrap>
        ),
      },
      {
        path: '/shield-runner',
        element: (
          <SuspenseWrap>
            <ShieldReportRunnerScreen />
          </SuspenseWrap>
        ),
      },
      {
        path: '/shield-playbook',
        element: (
          <SuspenseWrap>
            <ShieldPlaybookRunnerScreen />
          </SuspenseWrap>
        ),
      },
      {
        path: '/fx',
        element: (
          <SuspenseWrap>
            <FxManagementScreen />
          </SuspenseWrap>
        ),
      },
      {
        path: '/re-matrix',
        element: (
          <SuspenseWrap>
            <ReMatrixScreen />
          </SuspenseWrap>
        ),
      },
      {
        path: '/messages',
        element: (
          <SuspenseWrap>
            <SystemMessagesScreen />
          </SuspenseWrap>
        ),
      },
      {
        path: '/features',
        element: (
          <SuspenseWrap>
            <ExperimentalFeaturesScreen />
          </SuspenseWrap>
        ),
      },
      {
        path: '/experiments',
        element: (
          <SuspenseWrap>
            <ExperimentsScreen />
          </SuspenseWrap>
        ),
      },
      {
        path: '/export',
        element: (
          <SuspenseWrap>
            <ExportScreen />
          </SuspenseWrap>
        ),
      },
      {
        path: '/config',
        element: (
          <SuspenseWrap>
            <ConfigScreen />
          </SuspenseWrap>
        ),
      },
      {
        path: '/translations',
        element: (
          <SuspenseWrap>
            <TranslationsScreen />
          </SuspenseWrap>
        ),
      },
      {
        path: '/premium',
        element: (
          <SuspenseWrap>
            <PremiumScreen />
          </SuspenseWrap>
        ),
      },
      { path: '/audit', element: <Navigate to="/companies/audit" replace /> },
      { path: '/config/translations', element: <Navigate to="/translations" replace /> },
      { path: '/company-migration', element: <Navigate to="/" replace /> },
    ],
  },
]);
